import { useCallback, useEffect, useState } from 'react';
import { Modal as BsModal } from '@/components/common/AppModal';
import { Button, Form, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { AiModel, Topic } from '../types';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import {
  DUPLICATE_EXECUTION_MESSAGE,
  isDuplicateExecutionError,
} from '@/app/TrackerAi/utils/api-errors';
import { ExecutionAlert } from './executions/DuplicateExecutionNotice';
import { enqueueRunErrorMessage } from '@/app/TrackerAi/utils/prompt-run';
import { useProject } from '../context/ProjectContext';
import { countTopicWords, MAX_TOPIC_WORDS, topicNameError } from '../utils/topic-name';
import { DEFAULT_TOPIC_LANGUAGE, TOPIC_LANGUAGES } from '../utils/topic-languages';
import {
  DEFAULT_TOPIC_MARKET_COUNTRY,
  TOPIC_MARKET_COUNTRIES,
  hasTopicMarketScope,
} from '../utils/topic-market-countries';
import { ModelMultiPicker } from './ModelIcon';
import { CompetitorFields, useCompetitorForm } from './CompetitorForm';
import { NewPromptFields, useNewPromptForm } from './NewPromptForm';
import { getHelpDescription } from '../utils/help/help-content';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  wide?: boolean;
  persistent?: boolean;
  scrollable?: boolean;
  dialogClassName?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  wide,
  persistent,
  scrollable,
  dialogClassName,
  footer,
  children,
}: ModalProps) {
  return (
    <BsModal
      show={open}
      onHide={onClose}
      centered={!scrollable}
      size={wide ? 'lg' : undefined}
      scrollable={scrollable}
      dialogClassName={dialogClassName}
      backdrop={persistent ? 'static' : true}
      keyboard={!persistent}
    >
      <BsModal.Header closeButton={!persistent}>
        <div>
          <BsModal.Title className="fs-0">{title}</BsModal.Title>
          {subtitle && <p className="mb-0 mt-1 fs--1 text-500">{subtitle}</p>}
        </div>
      </BsModal.Header>
      <BsModal.Body>{children}</BsModal.Body>
      {footer && <BsModal.Footer className="d-block">{footer}</BsModal.Footer>}
    </BsModal>
  );
}

export interface NewPromptSubmitPayload {
  texts: string[];
  modelIds: string[];
  topicId?: string;
  /** When true, opens the last created prompt in the prompts inspector. Default: stay on current view. */
  navigateAfterCreate?: boolean;
  /** When true, waits for all /run calls to finish before resolving (e.g. setup onboarding). */
  awaitRuns?: boolean;
}

interface NewPromptModalProps {
  open: boolean;
  onClose: () => void;
  topics: Topic[];
  models: AiModel[];
  defaultModelId: string;
  defaultTopicId?: string | null;
  onSubmit: (payload: NewPromptSubmitPayload) => Promise<void>;
  saving: boolean;
}

export function NewPromptModal({
  open,
  onClose,
  topics,
  models,
  defaultModelId,
  defaultTopicId,
  onSubmit,
  saving,
}: NewPromptModalProps) {
  const form = useNewPromptForm({
    topics,
    models,
    defaultModelId,
    defaultTopicId,
    active: open,
  });

  async function handleSubmit() {
    const values = form.getValues();
    if (!values) return;
    try {
      await onSubmit(values);
      onClose();
    } catch {
      // parent handles error
    }
  }

  function handlePromptKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && form.canSubmit && !saving) {
      e.preventDefault();
      void handleSubmit();
    }
  }

  const saveLabel = saving
    ? 'در حال ذخیره…'
    : form.promptLines.length > 1
      ? `ذخیره (${form.promptLines.length})`
      : 'ذخیره';

  const footer = (
    <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-end gap-3">
      <div className="d-flex align-items-center justify-content-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="link"
          className="text-700 text-decoration-none"
          onClick={onClose}
          disabled={saving}
        >
          انصراف
        </Button>
        <Button
          type="submit"
          form="new-prompt-form"
          size="sm"
          variant="falcon-primary"
          disabled={saving || !form.canSubmit}
        >
          {saveLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="پرامپت جدید"
      subtitle="از AI سوالی بپرسید تا دیده‌شدن برند را در پاسخ ردیابی کنید. هر خط یک پرامپت جداگانه است."
      wide
      dialogClassName="tracker-ai-new-prompt-modal"
      footer={footer}
    >
      <style>{`
        .modal-lg.tracker-ai-new-prompt-modal {
          max-width: min(640px, calc(100vw - 1rem));
        }
      `}</style>
      <NewPromptFields
        form={form}
        formId="new-prompt-form"
        autoFocus
        showModelSearch={false}
        onTextKeyDown={handlePromptKeyDown}
        onSubmit={() => void handleSubmit()}
      />
    </Modal>
  );
}

interface NewTopicModalProps {
  open: boolean;
  onClose: () => void;
  models: AiModel[];
  defaultModelId: string;
  onCreateTopic: (payload: {
    name: string;
    language: string;
    marketCountry: string;
  }) => Promise<Pick<Topic, 'id' | 'name'>>;
  onComplete: () => Promise<void>;
  saving: boolean;
}

type SuggestedPrompt = {
  id: string;
  text: string;
  editing: boolean;
  selected: boolean;
};

export function NewTopicModal({
  open,
  onClose,
  models,
  defaultModelId,
  onCreateTopic,
  onComplete,
  saving,
}: NewTopicModalProps) {
  const { setError } = useProject();
  const [step, setStep] = useState<'name' | 'generating' | 'review'>('name');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<string>(DEFAULT_TOPIC_LANGUAGE);
  const [marketCountry, setMarketCountry] = useState<string>(DEFAULT_TOPIC_MARKET_COUNTRY);
  const [topic, setTopic] = useState<Pick<Topic, 'id' | 'name'> | null>(null);
  const [modelIds, setModelIds] = useState<string[]>([]);
  const [prompts, setPrompts] = useState<SuggestedPrompt[]>([]);
  const [generateError, setGenerateError] = useState('');
  const [actionError, setActionError] = useState('');
  const [runningId, setRunningId] = useState<string | null>(null);
  const [savingPrompts, setSavingPrompts] = useState(false);

  const nameError = name.trim() ? topicNameError(name) : null;
  const wordCount = countTopicWords(name);

  const reset = useCallback(() => {
    setStep('name');
    setName('');
    setLanguage(DEFAULT_TOPIC_LANGUAGE);
    setMarketCountry(DEFAULT_TOPIC_MARKET_COUNTRY);
    setTopic(null);
    setPrompts([]);
    setGenerateError('');
    setActionError('');
    setRunningId(null);
    setSavingPrompts(false);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    const initialModelId = defaultModelId || models[0]?.id || '';
    setModelIds(initialModelId ? [initialModelId] : []);
  }, [open, defaultModelId, models, reset]);

  async function loadSuggestions(created: Pick<Topic, 'id'>, modelId: string) {
    setStep('generating');
    setGenerateError('');
    try {
      const { suggestions } = await api.suggestTopicPrompts(created.id, modelId);
      setPrompts(
        suggestions.map((text) => ({
          id: crypto.randomUUID(),
          text,
          editing: false,
          selected: false,
        })),
      );
      setStep('review');
    } catch (e) {
      setGenerateError(e instanceof Error ? e.message : String(e));
      setStep('review');
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (nameError) return;
    setActionError('');
    try {
      const created = await onCreateTopic({
        name: name.trim(),
        language,
        marketCountry,
      });
      setTopic(created);
      const modelId = modelIds[0] || defaultModelId || models[0]?.id;
      if (!modelId) {
        setGenerateError('مدل AI برای تولید پرامپت در دسترس نیست.');
        setStep('review');
        return;
      }
      await loadSuggestions(created, modelId);
    } catch {
      // parent handles error
    }
  }

  function updatePromptText(id: string, text: string) {
    setPrompts((prev) => prev.map((p) => (p.id === id ? { ...p, text } : p)));
  }

  function toggleEdit(id: string) {
    setPrompts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, editing: !p.editing } : p)),
    );
  }

  function toggleSelected(id: string) {
    setPrompts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p)),
    );
  }

  function selectAllPrompts() {
    setPrompts((prev) => prev.map((p) => ({ ...p, selected: true })));
  }

  function rejectPrompt(id: string) {
    setPrompts((prev) => prev.filter((p) => p.id !== id));
  }

  async function runPrompt(item: SuggestedPrompt) {
    if (!topic) return;
    const modelId = modelIds[0] || defaultModelId || models[0]?.id;
    if (!modelId) return;
    setRunningId(item.id);
    setActionError('');
    try {
      const created = await api.createPrompt(
        topic.id,
        item.text.trim(),
        modelId,
        modelIds,
      );
      await api.runPrompt(created.id);
      setPrompts((prev) => prev.filter((p) => p.id !== item.id));
      await onComplete();
    } catch (e) {
      if (isDuplicateExecutionError(e)) {
        setActionError(DUPLICATE_EXECUTION_MESSAGE);
        return;
      }
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunningId(null);
    }
  }

  async function handleDone() {
    if (!topic) {
      onClose();
      return;
    }
    const modelId = modelIds[0] || defaultModelId || models[0]?.id;
    const remaining = prompts
      .filter((p) => p.selected)
      .map((p) => p.text.trim())
      .filter(Boolean);
    setSavingPrompts(true);
    setActionError('');
    try {
      if (remaining.length > 0 && modelId) {
        const created = await api.batchCreatePrompts(topic.id, modelIds, remaining);
        const ids = created?.ids ?? [];
        try {
          const result = await api.enqueuePromptRuns(
            ids.length > 0 ? { promptIds: ids } : { topicId: topic.id },
          );
          const enqueueError = enqueueRunErrorMessage(result);
          if (enqueueError) setError(enqueueError);
        } catch (e) {
          setError(
            isDuplicateExecutionError(e)
              ? DUPLICATE_EXECUTION_MESSAGE
              : e instanceof Error
                ? e.message
                : String(e),
          );
        }
      }
      await onComplete();
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingPrompts(false);
    }
  }

  function handleClose() {
    onClose();
  }

  if (step === 'name') {
    return (
      <Modal open={open} onClose={onClose} title="تاپیک جدید">
        <Form onSubmit={(e) => void handleCreate(e)} className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label className="fs--1 fw-medium">نام تاپیک</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="مثلاً: ابزارهای SEO"
              isInvalid={!!nameError}
            />
            <Form.Text className={nameError ? 'text-danger' : 'text-500'}>
              {nameError ?? `حداکثر ${MAX_TOPIC_WORDS} کلمه (${wordCount}/${MAX_TOPIC_WORDS})`}
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label className="fs--1 fw-medium">زبان</Form.Label>
            <Form.Select value={language} onChange={(e) => setLanguage(e.target.value)}>
              {TOPIC_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.nativeLabel} — {lang.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-500">
              AI پرامپت‌های کشف را به این زبان تولید می‌کند.
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label className="fs--1 fw-medium">بازار هدف</Form.Label>
            <Form.Select value={marketCountry} onChange={(e) => setMarketCountry(e.target.value)}>
              {TOPIC_MARKET_COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.label}
                </option>
              ))}
            </Form.Select>
            {hasTopicMarketScope(marketCountry) ? (
              <Form.Text className="text-500">
                پرامپت‌های کشف متناسب با بازار انتخاب‌شده بومی‌سازی می‌شوند.
              </Form.Text>
            ) : (
              <p className="fs--2 text-500 mt-2 rounded-2 border bg-100 px-3 py-2 lh-base mb-0">
                پرامپت‌ها بدون در نظر گرفتن منطقه یا کشور خاص تولید می‌شوند. به AI دستورالعمل
                بازار منطقه‌ای ارسال نمی‌شود.
              </p>
            )}
          </Form.Group>
          <p className="fs--2 text-500 mb-0">
            پس از ایجاد، AI پرامپت‌های کشف برای ردیابی برند و رقیب پیشنهاد می‌دهد.
          </p>
          <hr className="my-0 w-100 text-300" />
          <div className="d-flex justify-content-end gap-2">
            <Button type="button" size="sm" variant="outline-secondary" onClick={onClose}>
              انصراف
            </Button>
            <Button
              type="submit"
              size="sm"
              variant="falcon-primary"
              disabled={saving || !name.trim() || !!nameError}
            >
              {saving ? 'در حال ایجاد…' : 'ایجاد و تولید پرامپت‌ها'}
            </Button>
          </div>
        </Form>
      </Modal>
    );
  }

  if (step === 'generating') {
    return (
      <Modal open={open} onClose={() => undefined} title="در حال تولید پرامپت‌ها" wide>
        <div className="py-5 text-center d-flex flex-column align-items-center gap-3">
          <Spinner animation="border" variant="primary" />
          <p className="fs--1 text-700 mb-0">
            در حال ایجاد پرامپت‌های کشف برای <span className="fw-medium">{topic?.name}</span>…
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`پرامپت‌های ${topic?.name ?? 'تاپیک'}`}
      wide
      scrollable
      dialogClassName="modal-dialog-scrollable"
      footer={
        <div className="d-flex justify-content-end gap-2">
          <Button type="button" size="sm" variant="outline-secondary" onClick={handleClose}>
            بستن
          </Button>
          {prompts.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="falcon-primary"
              onClick={() => void handleDone()}
              disabled={savingPrompts || modelIds.length === 0 || prompts.every(p => !p.selected)}
            >
              {savingPrompts
                ? 'در حال ذخیره و اجرا…'
                : `ذخیره و اجرای ${prompts.filter(p => p.selected).length} پرامپت`}
            </Button>
          )}
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        <p className="fs--1 fw-medium text-700 mb-0">
          پرامپت‌ها را بررسی کنید؛ انتخاب‌شده‌ها ذخیره و با مدل‌های انتخاب‌شده اجرا می‌شوند. می‌توانید اجرا، ویرایش یا رد کنید.
        </p>

        {models.length > 0 && (
          <Form.Group>
            <Form.Label className="fs--1 fw-medium mb-0">مدل برای اجرا</Form.Label>
            <ModelMultiPicker
              models={models}
              value={modelIds}
              onChange={setModelIds}
              showSearch={false}
              showSelectedBadges={false}
            />
          </Form.Group>
        )}

        {generateError && (
          <Alert variant="warning" className="fs--1 mb-0 py-2">
            {generateError}
          </Alert>
        )}
        {actionError && (
          <ExecutionAlert message={actionError} className="fs--1 mb-0 py-2" />
        )}

        {prompts.length > 0 && (
          <div className="d-flex justify-content-end">
            <Button
              type="button"
              size="sm"
              variant="falcon-default"
              onClick={selectAllPrompts}
              disabled={prompts.every(p => p.selected)}
            >
              انتخاب همه
            </Button>
          </div>
        )}

        {prompts.length === 0 ? (
          <p className="fs--1 text-500 py-4 text-center mb-0">پرامپتی برای بررسی وجود ندارد.</p>
        ) : (
          <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
            {prompts.map((item, index) => (
              <li
                key={item.id}
                className={classNames(
                  'rounded-3 border px-2 py-2',
                  item.selected
                    ? 'border-300 bg-body'
                    : 'border-dashed border-300 bg-body-tertiary opacity-75',
                )}
              >
                <div className="d-flex align-items-start gap-2">
                  <Form.Check
                    type="checkbox"
                    id={`prompt-select-${item.id}`}
                    className="mt-1 mb-0 flex-shrink-0"
                    checked={item.selected}
                    onChange={() => toggleSelected(item.id)}
                    title="انتخاب برای ذخیره"
                    aria-label="انتخاب برای ذخیره"
                  />
                  <div className="flex-grow-1 min-w-0">
                    {item.editing ? (
                      <Form.Control
                        as="textarea"
                        value={item.text}
                        onChange={e => updatePromptText(item.id, e.target.value)}
                        rows={2}
                        className="fs--1"
                        style={{ resize: 'vertical' }}
                      />
                    ) : (
                      <p className="fs--1 text-800 lh-base mb-0">
                        <span className="fs--2 text-400 me-1">#{index + 1}</span>
                        {item.text}
                      </p>
                    )}
                  </div>
                  <div
                    className="d-flex flex-shrink-0 align-items-center gap-1 border-start ps-2 ms-1"
                    style={{ paddingTop: '0.125rem' }}
                  >
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`run-${item.id}`}>{getHelpDescription('actions.run_prompt')}</Tooltip>}
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="falcon-primary"
                        className="fs--2 py-0 px-2"
                        onClick={() => void runPrompt(item)}
                        disabled={runningId === item.id || modelIds.length === 0}
                        aria-label="اجرا"
                      >
                        <FontAwesomeIcon
                          icon={runningId === item.id ? 'spinner' : 'play'}
                          spin={runningId === item.id}
                        />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`edit-${item.id}`}>
                          {getHelpDescription(item.editing ? 'actions.finish_editing' : 'actions.edit_prompt_text')}
                        </Tooltip>
                      }
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="falcon-default"
                        className="fs--2 py-0 px-2"
                        onClick={() => toggleEdit(item.id)}
                        aria-label={item.editing ? 'پایان ویرایش' : 'ویرایش'}
                      >
                        <FontAwesomeIcon icon={item.editing ? 'check' : 'pen'} />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`reject-${item.id}`}>{getHelpDescription('actions.reject_prompt')}</Tooltip>}
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="falcon-danger"
                        className="fs--2 py-0 px-2"
                        onClick={() => rejectPrompt(item.id)}
                        aria-label="رد"
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

interface CompetitorModalProps {
  open: boolean;
  onClose: () => void;
  initial?: {
    brandName: string;
    websiteUrl: string;
    aliases: string[];
  };
  onSubmit: (data: { brandName: string; websiteUrl: string; aliases: string[] }) => Promise<void>;
  saving: boolean;
  title?: string;
}

export function CompetitorModal({
  open,
  onClose,
  initial,
  onSubmit,
  saving,
  title = 'افزودن رقیب',
}: CompetitorModalProps) {
  const form = useCompetitorForm({ active: open, initial });

  async function handleSubmit() {
    const values = form.getValues();
    if (!values) return;
    try {
      await onSubmit(values);
      onClose();
    } catch {
      // parent handles error
    }
  }

  const footer = (
    <div className="d-flex justify-content-end gap-2">
      <Button type="button" size="sm" variant="outline-secondary" onClick={onClose} disabled={saving}>
        انصراف
      </Button>
      <Button
        type="submit"
        form="competitor-form"
        size="sm"
        variant="primary"
        disabled={saving || !form.canSubmit}
      >
        {saving ? 'در حال ذخیره…' : 'ذخیره'}
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={title} footer={footer}>
      <CompetitorFields
        form={form}
        formId="competitor-form"
        autoFocus
        onSubmit={() => void handleSubmit()}
      />
    </Modal>
  );
}
